// ShowEditItemModal.jsx
import React, { useState, useEffect, useRef } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import makeAnimated from "react-select/animated";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";

import { customStyles } from "../../../Bookclosure/Components/AddTaskModal";
import commoncss from "./../../css/common.module.css";
import buttoncss from "./../../css/button.module.css";
import libcss from "./../../css/library.module.css";
import Modal from "./../../elements/Modal";
import { Button } from "./../../elements/Button";
import Dropdown from "./../../elements/Dropdown";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/formulaPlus.svg";
import { ReactComponent as AddPlusIcon } from "../../assets/icons/grayPlus.svg";
import { ReactComponent as MinusIcon } from "../../assets/icons/MinusIcon.svg";
import { ReactComponent as MultiplyIcon } from "../../assets/icons/MultiplyIcon.svg";
import { ReactComponent as DivisionIcon } from "../../assets/icons/DivisionIcon.svg";
import InputGroup from "./../../elements/InputGroup";
import CustomDropdownIndicator from "../../elements/CustomDropdownIndicator";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import adminPortalCss from "../../css/adminportal.module.css";

const operatorOptions = [
  { value: "positive", label: "plus", symbol: "+", Icon: PlusIcon },
  { value: "negative", label: "minus", symbol: "−", Icon: MinusIcon },
  // { value: "multiply", label: "multiply", symbol: "×", Icon: MultiplyIcon },
  // { value: "divide", label: "divide", symbol: "÷", Icon: DivisionIcon },
];

const ShowEditItemModal = ({ groupingData, item, level, context = {}, onSave, onClose }) => {
  // keep original UI/state names
  const [selectedItem, setSelectedItem] = useState(item?.lineType ?? "line item");
  const [itemName, setItemName] = useState(item?.name ?? "");
  const [lineItemNature, setLineItemNature] = useState(item?.nature ?? "credit");

  // each formula element stores { element: id, value: optionObject, operator }
  const [formulaItems, setFormulaItems] = useState([]);

  // 🔸 LEVEL TO MATCH = item.level (fallback to level prop)
  const levelToMatch = item?.level ?? level;

  // 🔹 restrict the search ROOT to the branch we are editing (that particular object)
  const getRootItemsForFormula = () => {
    if (!Array.isArray(groupingData)) return [];

    // Level 1 → whole top-level list
    if (levelToMatch === 1) return groupingData;

    // Level 2 → subgroups of the selected level-1 node
    if (levelToMatch === 2) {
      const l1id = context?.l1id;
      if (!l1id) return groupingData;
      const l1 = groupingData.find((g) => g._id === l1id);
      return Array.isArray(l1?.subgroup) ? l1.subgroup : [];
    }

    // Level 3 → subgroups of the selected level-2 node inside the correct level-1
    if (levelToMatch === 3) {
      const l1id = context?.l1id;
      const l2id = context?.l2id;
      if (!l1id || !l2id) return groupingData;

      const l1 = groupingData.find((g) => g._id === l1id);
      const l2 = Array.isArray(l1?.subgroup)
        ? l1.subgroup.find((sg) => sg._id === l2id)
        : null;

      return Array.isArray(l2?.subgroup) ? l2.subgroup : [];
    }

    // fallback
    return groupingData;
  };

  // Build selectable items for formula builder (mirrors AddItemModal.extractItems)
  // 🔧 CHANGED: added rootCategory param and pass it into traverse()
  // BEFORE: had category filtering that killed level-3 items
// const extractItems = (items = [], lvlToMatch, categoryToMatch, rootCategory = null) => {
const extractItems = (items = [], lvlToMatch) => {
  const result = [];
  const seen = new Set();

  const traverse = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      // ✅ only same level, and only line items (skip formulas like "Total")
      if (node.level === lvlToMatch && node.lineType === "line item") {
        const key = `${node.level}||${node.name}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({ label: node.name, value: node.name });
        }
      }

      if (Array.isArray(node.subgroup) && node.subgroup.length) {
        traverse(node.subgroup);
      }
    }
  };

  traverse(items);
  return result;
};

  const guessCategoryForFormula = () => {
    if (!groupingData) return null;
    if (context?.category) return context.category;
    const l1id = context?.l1id ?? null;
    if (l1id) {
      const l1 = groupingData.find((g) => g._id === l1id);
      if (l1 && l1.category) return l1.category;
    }
    if (item?.category) return item.category;
    return null;
  };

const formulaItemList = extractItems(
  getRootItemsForFormula(),
  levelToMatch
);


  // Helper: get canonical option object for a name (or for an incoming option-like object)
  const canonicalOptionFor = (incoming) => {
    if (!incoming && incoming !== "") return null;
    // if incoming is an object with label/value, try to match by value or label
    if (typeof incoming === "object") {
      const name = incoming.value ?? incoming.label ?? null;
      if (name == null) return incoming;
      const found = formulaItemList.find(
        (o) => o.value === name || o.label === name
      );
      return found ?? { label: name, value: name };
    }
    // primitive: string -> match
    if (typeof incoming === "string") {
      const found = formulaItemList.find(
        (o) => o.value === incoming || o.label === incoming
      );
      return found ?? { label: incoming, value: incoming };
    }
    return null;
  };

  // initialize formulaItems from item.formula (if present)
  useEffect(() => {
    // populate basic fields
    setSelectedItem(item?.lineType ?? "line item");
    setItemName(item?.name ?? "");
    setLineItemNature(item?.nature ?? "credit");

    if (
      (item?.formula && Array.isArray(item.formula) && item.formula.length > 0) ||
      item?.lineType === "formula"
    ) {
      // map saved formula names into option objects (from formulaItemList), fallback if missing
      const mapped =
        Array.isArray(item?.formula) && item.formula.length > 0
          ? item.formula.map((f, idx) => ({
              element: Date.now() + idx,
              value: canonicalOptionFor(f.name ?? ""),
              operator: f.sign ?? "positive",
            }))
          : [{ element: Date.now(), value: null, operator: "positive" }];

      setFormulaItems(mapped);
    } else {
      setFormulaItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, groupingData]);

  const addFormulaItem = () => {
    setFormulaItems((prev) => [
      ...prev,
      {
        element: Date.now() + Math.floor(Math.random() * 1000),
        value: null,
        operator: "positive",
      },
    ]);
  };

  // Accept option object OR primitive; normalize into option object for state
  const updateFormulaItem = (index, incoming) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], value: canonicalOptionFor(incoming) };
      return copy;
    });
  };

  const updateFormulaOperator = (index, operator) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], operator };
      return copy;
    });
  };

  // OperatorDropdown component (keeps same visual as earlier)
  const OperatorDropdown = ({ value, onSelect, className = "", style = {} }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected =
      operatorOptions.find((o) => o.value === value) ?? operatorOptions[0];

    useEffect(() => {
      const handleDocClick = (e) => {
        if (!ref.current) return;
        if (!ref.current.contains(e.target)) setOpen(false);
      };
      const handleEsc = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", handleDocClick);
      document.addEventListener("keydown", handleEsc);
      return () => {
        document.removeEventListener("mousedown", handleDocClick);
        document.removeEventListener("keydown", handleEsc);
      };
    }, []);

    const IconComp = selected?.Icon ?? PlusIcon;

    return (
      <div
        ref={ref}
        className={className}
        style={{ position: "relative", display: "inline-block", ...style }}
      >
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          aria-haspopup="menu"
          aria-expanded={open}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            border: "1px solid #fff",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <IconComp />
          </span>
        </button>

        {open && (
          <div
            role="menu"
            aria-label="Choose operator"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              background: "#fff",
              border: "1px solid #ddd",
              boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
              borderRadius: 6,
              padding: 6,
              zIndex: 50,
            }}
          >
            {operatorOptions.map((op) => {
              const Icon = op.Icon ?? PlusIcon;
              return (
                <div
                  key={op.value}
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => {
                    onSelect(op.value);
                    setOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(op.value);
                      setOpen(false);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    cursor: "pointer",
                    borderRadius: 4,
                    width: "fit-content",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <Icon />
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // build formula display string
  const formulaDisplayString = (() => {
    if (!formulaItems || formulaItems.length === 0) return "";
    const pieces = formulaItems.map((it) => {
      if (!it || !it.value) return "(?)";
      if (typeof it.value === "string") return it.value;
      if (it.value.label) return it.value.label;
      return it.value.value ?? "(?)";
    });
    let display = "";
    for (let i = 0; i < pieces.length; i++) {
      display += pieces[i];
      if (i < pieces.length - 1) {
        const op = operatorOptions.find(
          (o) => o.value === formulaItems[i].operator
        );
        display += ` ${op?.symbol ?? "+"} `;
      }
    }
    return display;
  }) ();

  const handleSave = () => {
    if (!itemName || itemName.trim() === "") {
      toast.error("Please enter a name");
      return;
    }

    const updated = {};
    if (itemName !== (item?.name ?? "")) updated.name = itemName;
    if (selectedItem !== (item?.lineType ?? "line item"))
      updated.lineType = selectedItem;
    if (
      selectedItem === "line item" &&
      lineItemNature !== (item?.nature ?? "credit")
    ) {
      updated.nature = lineItemNature;
    }

    if (selectedItem === "formula") {
      const returnFormulaItems = formulaItems
        .filter(
          (x) =>
            x.value &&
            (x.value.value || x.value.label || typeof x.value === "string")
        )
        .map((x) => ({
          name:
            typeof x.value === "string"
              ? x.value
              : x.value.value ?? x.value.label ?? "",
          sign: x.operator ?? "positive",
        }));
      updated.formula = returnFormulaItems;
    }

    onSave && onSave(updated);
  };

  return (
    <Modal
      width={"45%"}
      minHeight={"200px"}
      title={`Edit ${selectedItem === "formula" ? "Formula" : "Line Item"}`}
      bodycss={commoncss.unsetPadding}
      headerActions={
        <>
          <CloseIcon
            className={commoncss.clickable}
            onClick={() => {
              onClose && onClose();
            }}
          />
        </>
      }
      onClose={() => {}}
      footerActions={
        <>
          <Button
            btnColor={buttoncss.secondary}
            onClick={() => onClose && onClose()}
            label="Cancel"
          />
          <span>
            &ensp;
            <Button
              btnColor={buttoncss.primary}
              label="Save"
              onClick={handleSave}
            />
          </span>
        </>
      }
    >
      <div className={commoncss.pd1530}>
        <div className={adminPortalCss.radioButtonSection}>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="responsibilities"
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            className={adminPortalCss.radioSection}
          >
            <FormControlLabel
              value="line item"
              className={adminPortalCss.radioLabel}
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label="Line Item"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  textTransform: "none",
                  color: "#111111",
                  fontSize: "14px",
                },
              }}
            />
            <FormControlLabel
              className={adminPortalCss.radioLabel}
              value="formula"
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label="Formula"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  textTransform: "none",
                  color: "#111111",
                  fontSize: "14px",
                },
              }}
            />
          </RadioGroup>
        </div>

        {selectedItem === "line item" && (
          <>
            <InputGroup
              class_="mb-2"
              label="Line Item Name"
              inputs={[
                <>
                  <input
                    name="line item name"
                    value={itemName ?? ""}
                    onChange={(e) => setItemName(e.target.value)}
                    placeholder="Enter line item name"
                  />
                </>,
              ]}
            />
            {/* Only show nature control for level 1 (same as AddItemModal behavior) */}
            {levelToMatch === 1 && (
              <div className={commoncss.dFlexJustifysb}>
                <span className={`${commoncss.label3} ${commoncss.fs13}`}>
                  Nature of Line Item
                </span>
                <div className={adminPortalCss.radioButtonSection}>
                  <RadioGroup
                    row
                    name="lineItemNature"
                    value={lineItemNature}
                    onChange={(e) => setLineItemNature(e.target.value)}
                    className={adminPortalCss.radioSection}
                  >
                    <FormControlLabel
                      value="credit"
                      className={adminPortalCss.radioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label="Credit"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 500,
                          textTransform: "none",
                          color: "#111111",
                          fontSize: "13px",
                        },
                      }}
                    />
                    <FormControlLabel
                      value="debit"
                      className={adminPortalCss.radioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label="Debit"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 500,
                          textTransform: "none",
                          color: "#111111",
                          fontSize: "13px",
                        },
                      }}
                    />
                  </RadioGroup>
                </div>
              </div>
            )}
          </>
        )}

        {selectedItem === "formula" && (
          <div>
            <InputGroup
              class_="mb-2"
              label="Formula Name"
              inputs={[
                <>
                  <input
                    name="Formula name"
                    value={itemName ?? ""}
                    onChange={(e) => setItemName(e.target.value)}
                    placeholder="Enter formula name"
                  />
                </>,
              ]}
            />

            <span className={`input-label ml-1`}>Build Formula</span>

            <div
              className={`${commoncss.dFlex} ${commoncss.gap20} ${
                libcss.formulaWrap || ""
              }`}
            >
              {formulaItems.map((fitem, index) => (
                <React.Fragment key={fitem.element ?? index}>
                  <div
                    className={commoncss.dFlex}
                    style={{ alignItems: "center", gap: "10px" }}
                  >
                    <div className={libcss.formulaCard}>
                      <span
                        className={`${commoncss.label3} ${commoncss.fw600}`}
                      >
                        LEVEL {levelToMatch}
                      </span>

                      <Dropdown
                        class2_={commoncss.bgWhite}
                        options={formulaItemList}
                        name="formula"
                        selected={fitem.value}
                        onSelect={(value) => updateFormulaItem(index, value)}
                        placeHolder="Select item"
                      />
                    </div>

                    {/* INLINE OPERATOR DROPDOWN */}
                    <OperatorDropdown
                      value={fitem.operator}
                      onSelect={(value) => updateFormulaOperator(index, value)}
                    />
                  </div>
                </React.Fragment>
              ))}

              <div
                className={`${libcss.addFormulaCard} ${commoncss.clickable}`}
                onClick={addFormulaItem}
              >
                <span className={`${commoncss.label3} ${commoncss.fw600}`}>
                  <AddPlusIcon />
                </span>
                <span className={`${commoncss.greyLabel}`}>Add Element</span>
              </div>
            </div>

            <div className={`${libcss.displayFormula} mt-3`}>
              <span className={`${commoncss.greyLabel} ${commoncss.fw500}`}>
                Formula
              </span>
              <span className={`${commoncss.label5} ${commoncss.fw500}`}>
                Formula Name ={" "}
                {formulaDisplayString || "(no formula selected)"}
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ShowEditItemModal;